import { Typography } from '@mui/material';
import { tokens } from '../../theme';
import type { TextBlockProps } from './types';

/**
 * Text that has to stay exactly as written — a prompt, a case's input — on a
 * recessed ground in the mono face.
 *
 * **It wraps rather than scrolls sideways.** A prompt is prose, and a long line
 * that needs a horizontal scrollbar is a line nobody finishes reading; `pre-wrap`
 * keeps every break and indent the author wrote and still fits the column. Only a
 * long block scrolls, vertically, when the caller caps it.
 *
 * **It is focusable when it scrolls**, so a keyboard user can reach the rest of
 * it: a region that scrolls and takes no focus is one only a pointer can read.
 */
export const TextBlock = ({ children, label, maxHeight }: TextBlockProps) => (
  <Typography
    component="pre"
    role="region"
    aria-label={label}
    tabIndex={maxHeight === undefined ? undefined : 0}
    sx={{
      fontFamily: tokens.fontFamily.mono,
      fontSize: 13,
      lineHeight: 1.6,
      backgroundColor: 'surface.sunken',
      borderRadius: `${String(tokens.radius.md)}px`,
      padding: 4,
      margin: 0,
      whiteSpace: 'pre-wrap',
      overflowWrap: 'anywhere',
      ...(maxHeight === undefined ? {} : { maxHeight, overflowY: 'auto' }),
    }}
  >
    {children}
  </Typography>
);
